/**
 * NotionEditor - Tiptap 公式 "Notion-like" テンプレートをラップした再利用可能エディタ
 *
 * - entity_type + entity_id で汎用テキストドキュメント(TextDocument)に紐付く
 *   （"memo" 以外でも任意の種別を使用可能。例: TODO のディスクリプション・
 *     アクティビティのメモなど）
 * - 単一ユーザー想定。Tiptap JSON を正本として扱い、保存は JSON Patch (RFC 6902) で
 *   差分のみを PATCH 送信する (通信量削減)
 * - 編集消失防止のため、保存中・保存失敗中の最新 JSON は localStorage に常時バックアップ
 *   次に開いたときに未送信分を再送する
 * - 他の画面で更新されていたら (409)、この画面の編集をサーバの最新に当て直して保存する。
 *   同じ箇所が両方で変わっていた場合だけ全文を上書きする (上書き前の本文はサーバの履歴に残る)
 * - 他ページ/別端末/AI追記等による更新を取り込むため、visibility/focus 復帰時 + 可視中
 *   30秒ごとに軽量 version エンドポイントで変更検知し、増えていればフル GET → setContent
 *   で反映する（ただしフォーカス中・保存待機中・未送信中はスキップ）
 *
 * @example メモページ（全画面）
 * ```tsx
 * <NotionEditor entityType="memo" entityId={memoId} onStatusChange={setStatus} />
 * ```
 *
 * @example TODO のディスクリプション（埋め込み・高さ制限）
 * ```tsx
 * <NotionEditor
 *   entityType="todo"
 *   entityId={todoId}
 *   placeholder="詳細を入力…"
 *   className="min-h-40 rounded-md border px-3 py-2"
 * />
 * ```
 */

import type { Editor, JSONContent } from '@tiptap/react'
import { compare } from 'fast-json-patch'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import {
  getTextDocument,
  getTextDocumentVersion,
  type JsonPatchOperation,
  patchTextDocument,
  saveTextDocument,
} from '@/api/services/memo'
import { AmazonPasteDialog } from '@/components/shared/amazon-paste-dialog'
import { TocProvider } from '@/components/tiptap-node/toc-node/context/toc-context'
import { EditorProvider } from '@/components/tiptap-templates/notion-like/notion-like-editor'
import { Button } from '@/components/ui/button'
import { isSingleAmazonUrlPaste } from '@/lib/amazon-url'
import { clearNotionEditorBackup as clearBackup, notionEditorBackupKey } from '@/lib/notion-editor-backup'
import { rebaseDocument } from '@/lib/notion-editor-rebase'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

type NotionEditorProps = {
  /** 紐付け対象種別（例: "memo" / "todo" / "activity"） */
  entityType: string
  /** 紐付け対象ID */
  entityId: number
  /** 編集可能か */
  editable?: boolean
  /** プレースホルダ */
  placeholder?: string
  /** 保存ステータス変化通知 */
  onStatusChange?: (status: SaveStatus) => void
  /** エディタ生成時に Editor インスタンスを受け取るコールバック (外部からテンプレ挿入等を行う用途) */
  onEditorReady?: (editor: Editor) => void
  /** 外側コンテナに付与する追加クラス（埋め込み時の高さ・枠線などの調整用） */
  className?: string
}

const SAVE_DEBOUNCE_MS = 1500
/** 可視中に走らせるサーバ変更検知ポーリング間隔 */
const REFRESH_POLL_INTERVAL_MS = 30_000
/** 409 で当て直して再送する回数の上限 */
const MAX_SAVE_ATTEMPTS = 3
const EMPTY_DOC: JSONContent = { type: 'doc', content: [] }

type BackupData = {
  json: JSONContent
  text: string
  /** サーバ最新 version に対応する patch 適用前バージョン */
  baseVersion: number
  /** baseVersion 時点のサーバの本文 (編集を当て直す基準)。旧形式のバックアップには無い */
  baseJson?: JSONContent
  /** true: PATCH 送信待ち / 失敗中 (オンライン復帰時に再送が必要) */
  pending: boolean
}

const parseContent = (raw: string | null): JSONContent => {
  if (!raw) return EMPTY_DOC
  try {
    return JSON.parse(raw) as JSONContent
  } catch {
    return EMPTY_DOC
  }
}

const replaceContentKeepingSelection = (editor: Editor, json: JSONContent): void => {
  const { from, to } = editor.state.selection
  editor.commands.setContent(json, { emitUpdate: false })
  const max = editor.state.doc.content.size
  editor.commands.setTextSelection({ from: Math.min(from, max), to: Math.min(to, max) })
}

const readBackup = (entityType: string, entityId: number): BackupData | null => {
  try {
    const raw = localStorage.getItem(notionEditorBackupKey(entityType, entityId))
    if (!raw) return null
    return JSON.parse(raw) as BackupData
  } catch {
    return null
  }
}

const writeBackup = (entityType: string, entityId: number, data: BackupData): void => {
  try {
    localStorage.setItem(notionEditorBackupKey(entityType, entityId), JSON.stringify(data))
  } catch {
    // localStorage 容量超過等は無視
  }
}

export const NotionEditor = ({
  entityType,
  entityId,
  editable = true,
  placeholder = '入力してください…',
  onStatusChange,
  onEditorReady,
  className,
}: NotionEditorProps) => {
  const editorRef = useRef<Editor | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)
  /** サーバから取得した最新 version (PATCH の expected_version に渡す) */
  const versionRef = useRef<number>(0)
  /** 直近サーバに送信(あるいは復元)した content_json (差分計算の base) */
  const lastSentJsonRef = useRef<JSONContent>(EMPTY_DOC)
  /** 未送信のバックアップを開いたとき、エディタ生成後に保存を走らせる */
  const needsFlushRef = useRef(false)
  /** 初期コンテンツ。null の間はエディタを生成しない (undo 履歴に初期 setContent を残さないため) */
  const [initialContent, setInitialContent] = useState<JSONContent | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadKey, setLoadKey] = useState(0)
  /**
   * 貼り付けられた Amazon URL の確認ダイアログ状態。
   * URL 単体貼り付けを検知した時に URL を保持し、確認 UI を出す。
   */
  const [pendingAmazonUrl, setPendingAmazonUrl] = useState<string | null>(null)

  // 保存処理: 差分があれば PATCH 送信。失敗時は localStorage に保留状態で残す
  const persist = useCallback(async () => {
    const editor = editorRef.current
    if (!editor) return

    const markSaved = (version: number, json: JSONContent) => {
      versionRef.current = version
      lastSentJsonRef.current = json
      clearBackup(entityType, entityId)
      if (isMountedRef.current) onStatusChange?.('saved')
    }

    for (let attempt = 0; attempt < MAX_SAVE_ATTEMPTS; attempt++) {
      const currentJson = editor.getJSON()
      const currentText = editor.getText()
      const patches = compare(lastSentJsonRef.current, currentJson)
      if (patches.length === 0) {
        if (attempt > 0) markSaved(versionRef.current, currentJson)
        return
      }

      onStatusChange?.('saving')

      // 送信前にバックアップを保留状態で書き込む (失敗してもこれが残る)
      writeBackup(entityType, entityId, {
        json: currentJson,
        text: currentText,
        baseVersion: versionRef.current,
        baseJson: lastSentJsonRef.current,
        pending: true,
      })

      let status: number | undefined
      try {
        const res = await patchTextDocument(entityType, entityId, {
          patches: patches as JsonPatchOperation[],
          expected_version: versionRef.current,
          content_text: currentText,
        })
        markSaved(res.version, currentJson)
        return
      } catch (error) {
        status = (error as { response?: { status?: number } })?.response?.status
      }

      try {
        if (status === 404) {
          const saved = await saveTextDocument(entityType, entityId, {
            content_json: JSON.stringify(currentJson),
            content_text: currentText,
          })
          markSaved(saved.version, currentJson)
          return
        }
        // アンマウント後は保留中のバックアップを残し、次に開いたときに当て直す
        if (status !== 409 || !isMountedRef.current) break

        const fresh = await getTextDocument(entityType, entityId)
        if (!isMountedRef.current || editor.isDestroyed) break
        const freshJson = parseContent(fresh.content_json)
        const localJson = editor.getJSON()
        const merged = rebaseDocument(lastSentJsonRef.current, localJson, freshJson)
        versionRef.current = fresh.version
        lastSentJsonRef.current = freshJson
        if (merged) {
          if (compare(localJson, merged).length > 0) replaceContentKeepingSelection(editor, merged)
          continue
        }

        // 同じ箇所が他の画面でも変わっていた。上書き前の本文はサーバの履歴に残る
        const saved = await saveTextDocument(entityType, entityId, {
          content_json: JSON.stringify(localJson),
          content_text: editor.getText(),
        })
        markSaved(saved.version, localJson)
        toast.warning('他の画面での変更と重なったため、この画面の内容で保存しました。以前の内容は履歴から戻せます')
        return
      } catch {
        break
      }
    }
    if (isMountedRef.current) onStatusChange?.('error')
  }, [entityType, entityId, onStatusChange])

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null
      void persist()
    }, SAVE_DEBOUNCE_MS)
  }, [persist])

  const persistRef = useRef(persist)
  useEffect(() => {
    persistRef.current = persist
  }, [persist])

  /**
   * サーバ最新を取り込む（他ページ・別端末・AI追記等で更新された場合）
   *
   * Yjs を廃止した現状、エディタへの反映は setContent による全置換しか手段がない。
   * 入力中に置換するとカーソル/選択/IME がリセットされ編集消失につながるため、
   * 以下のいずれかに該当する間は反映をスキップする:
   *   - 保存 debounce 待機中 (saveTimerRef あり)
   *   - 未送信のローカルバックアップが残っている (pending)
   *   - エディタがフォーカス中
   *   - エディタ未初期化
   * スキップした場合は次のトリガ(focus/visibility/次のpoll)で再試行する。
   */
  const refreshFromServer = useCallback(async () => {
    const editor = editorRef.current
    if (!editor) return
    if (saveTimerRef.current) return
    if (editor.isFocused) return

    const backup = readBackup(entityType, entityId)
    if (backup?.pending) return

    try {
      const head = await getTextDocumentVersion(entityType, entityId)
      if (editorRef.current !== editor) return
      if (head.version <= versionRef.current) return

      const fresh = await getTextDocument(entityType, entityId)
      if (!isMountedRef.current) return
      if (editorRef.current !== editor) return

      // 取得中にユーザーが触り始めていたら今回は見送る
      if (saveTimerRef.current || editor.isFocused) return

      const nextJson = parseContent(fresh.content_json)
      editor.commands.setContent(nextJson, { emitUpdate: false })
      versionRef.current = fresh.version
      lastSentJsonRef.current = nextJson
    } catch {
      // ネットワークエラー等は次のトリガで再試行
    }
  }, [entityType, entityId])

  const refreshFromServerRef = useRef(refreshFromServer)
  useEffect(() => {
    refreshFromServerRef.current = refreshFromServer
  }, [refreshFromServer])

  // 初期ロード: サーバ取得 + localStorage バックアップ整合
  useEffect(() => {
    isMountedRef.current = true
    setInitialContent(null)

    const load = async () => {
      let server: { json: JSONContent; version: number } | null = null
      try {
        const doc = await getTextDocument(entityType, entityId)
        server = { json: parseContent(doc.content_json), version: doc.version }
      } catch {
        // 取得失敗: 未送信のバックアップがあればそれを開く
      }
      if (!isMountedRef.current) return

      const backup = readBackup(entityType, entityId)
      const pendingBase = backup?.pending ? (backup.baseJson ?? server?.json) : undefined
      if (backup && pendingBase) {
        // 未送信の編集を開いて保存する。サーバが先に進んでいれば 409 になり、最新に当て直す
        versionRef.current = backup.baseVersion
        lastSentJsonRef.current = pendingBase
        needsFlushRef.current = true
        setInitialContent(backup.json)
        return
      }
      // 空のエディタで開くと、書いた内容で既存の本文を上書きしかねないので開かない
      if (!server) {
        setLoadFailed(true)
        return
      }
      if (backup) clearBackup(entityType, entityId)
      versionRef.current = server.version
      lastSentJsonRef.current = server.json
      setInitialContent(server.json)
    }
    void load()

    return () => {
      isMountedRef.current = false
    }
  }, [entityType, entityId, loadKey])

  // 可視/フォーカス復帰 + 可視中の定期ポーリングでサーバ最新を反映
  useEffect(() => {
    if (!editable) return

    let intervalId: ReturnType<typeof setInterval> | null = null

    const startPolling = () => {
      if (intervalId !== null) return
      intervalId = setInterval(() => {
        void refreshFromServerRef.current()
      }, REFRESH_POLL_INTERVAL_MS)
    }

    const stopPolling = () => {
      if (intervalId === null) return
      clearInterval(intervalId)
      intervalId = null
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshFromServerRef.current()
        startPolling()
      } else {
        stopPolling()
      }
    }

    const handleFocus = () => {
      void refreshFromServerRef.current()
    }

    if (document.visibilityState === 'visible') startPolling()
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', handleFocus)

    return () => {
      stopPolling()
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', handleFocus)
    }
  }, [editable])

  // アンマウント時: 保留中の保存を確定
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        void persistRef.current()
      }
    }
  }, [])

  // エディタ生成時: インスタンスを保持
  const handleCreate = useCallback(
    ({ editor }: { editor: Editor }) => {
      editorRef.current = editor
      onEditorReady?.(editor)
      if (needsFlushRef.current) {
        needsFlushRef.current = false
        scheduleSave()
      }

      // Amazon URL 単体貼り付けを検知して確認ダイアログを開く。
      // ProseMirror の editable 状態に関わらず一律で購読するが、editable=false 時は
      // そもそも paste イベントが飛んでこないため実質編集中のみ発火する。
      const handlePaste = (event: ClipboardEvent) => {
        const text = event.clipboardData?.getData('text/plain') ?? ''
        const url = isSingleAmazonUrlPaste(text)
        if (!url) return
        // ダイアログの結果を待つため貼り付け自体は一旦キャンセルする
        event.preventDefault()
        event.stopPropagation()
        setPendingAmazonUrl(url)
      }

      editor.view.dom.addEventListener('paste', handlePaste, { capture: true })
      // Editor destroy 時に自動解除される (view.dom は destroy 前に無効化されるが
      // 参照は残るため明示的に off しておく)
      editor.on('destroy', () => {
        editor.view.dom.removeEventListener('paste', handlePaste, { capture: true })
      })
    },
    [onEditorReady, scheduleSave],
  )

  /** Amazon URL の変換キャンセル: URL テキストを普通に挿入して閉じる */
  const handleAmazonPasteCancel = useCallback(() => {
    const editor = editorRef.current
    const url = pendingAmazonUrl
    setPendingAmazonUrl(null)
    if (!editor || !url) return
    editor.chain().focus().insertContent(url).run()
  }, [pendingAmazonUrl])

  /** Amazon URL の変換確定: カスタムノードを挿入して閉じる */
  const handleAmazonPasteConfirm = useCallback(
    (attrs: {
      asin: string | null
      url: string
      title: string
      image: string | null
      price: string | null
      brand: string | null
      rating: number | null
      reviewCount: number | null
      fetchedAt: string
    }) => {
      const editor = editorRef.current
      setPendingAmazonUrl(null)
      if (!editor) return
      editor.chain().focus().insertContent({ type: 'amazonProduct', attrs }).run()
    },
    [],
  )

  // 編集更新時: 編集可能なら debounce 保存 (EditorProvider は initialContent が確定してから生成されるため、
  //              初期コンテンツ起因の onUpdate は発火しない)
  const handleUpdate = useCallback(() => {
    if (!editable) return
    scheduleSave()
  }, [editable, scheduleSave])

  return (
    <TocProvider>
      <div className={className}>
        {loadFailed && (
          <div className="flex flex-col items-center gap-2 py-8 text-sm text-muted-foreground">
            <p>本文を読み込めませんでした</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setLoadFailed(false)
                setLoadKey((k) => k + 1)
              }}
            >
              再読み込み
            </Button>
          </div>
        )}
        {initialContent !== null && (
          <EditorProvider
            content={initialContent}
            editable={editable}
            placeholder={placeholder}
            onCreate={handleCreate}
            onUpdate={handleUpdate}
          />
        )}
      </div>
      <AmazonPasteDialog
        url={pendingAmazonUrl}
        onCancel={handleAmazonPasteCancel}
        onConfirm={handleAmazonPasteConfirm}
      />
    </TocProvider>
  )
}
